import React, { useState, useEffect, useRef } from 'react';
import cogoToast from 'cogo-toast';
import moment from 'moment';
import Select from 'react-select';
import AsyncSelect from 'react-select/async';
import SelectOption from '../General/SelectOption';
import { getBrandBasicObjects } from '../../api/brands';
import {
  getManualOrderAdjustments,
  createManualOrderAdjustment,
  previewManualOrderAdjustmentsCsv,
  createManualOrderAdjustmentsBulk
} from '../../api/affiliate_commissions';
import { getSearchIndex } from '../../helpers/search_helpers';
import ManualOrderAdjustmentsCsvModal from './ManualOrderAdjustmentsCsvModal';
import VoidCreatorCommissions from './VoidCreatorCommissions';
import './ManualOrderAdjustments.scss';

const searchIndex = getSearchIndex('sms_users_production');

const CURRENCY_OPTIONS = [
  { value: 'USD', label: 'USD — US Dollar' },
  { value: 'EUR', label: 'EUR — Euro' },
  { value: 'GBP', label: 'GBP — British Pound' },
  { value: 'CAD', label: 'CAD — Canadian Dollar' },
  { value: 'AUD', label: 'AUD — Australian Dollar' },
  { value: 'NZD', label: 'NZD — New Zealand Dollar' },
  { value: 'SEK', label: 'SEK — Swedish Krona' },
  { value: 'NOK', label: 'NOK — Norwegian Krone' },
  { value: 'DKK', label: 'DKK — Danish Krone' },
  { value: 'CHF', label: 'CHF — Swiss Franc' },
  { value: 'JPY', label: 'JPY — Japanese Yen' },
  { value: 'CNY', label: 'CNY — Chinese Yuan' },
  { value: 'KRW', label: 'KRW — South Korean Won' },
  { value: 'INR', label: 'INR — Indian Rupee' },
  { value: 'BRL', label: 'BRL — Brazilian Real' },
  { value: 'MXN', label: 'MXN — Mexican Peso' },
  { value: 'SGD', label: 'SGD — Singapore Dollar' },
  { value: 'HKD', label: 'HKD — Hong Kong Dollar' },
  { value: 'ZAR', label: 'ZAR — South African Rand' },
  { value: 'AED', label: 'AED — UAE Dirham' },
  { value: 'ILS', label: 'ILS — Israeli Shekel' },
  { value: 'PLN', label: 'PLN — Polish Zloty' },
  { value: 'TRY', label: 'TRY — Turkish Lira' }
];

const DEFAULT_CURRENCY = CURRENCY_OPTIONS[0];

const EMPTY_FORM = {
  order_id: '',
  order_amount: '',
  commission_rate: '',
  transaction_date: '',
  code: ''
};

const ManualOrderAdjustments = () => {
  const csvInputRef = useRef(null);
  const [form, setForm] = useState(EMPTY_FORM);
  const [selectedBrand, setSelectedBrand] = useState(null);
  const [selectedCreator, setSelectedCreator] = useState(null);
  const [selectedCurrency, setSelectedCurrency] = useState(DEFAULT_CURRENCY);
  const [brandOptions, setBrandOptions] = useState([]);
  const [adjustments, setAdjustments] = useState([]);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isFetching, setIsFetching] = useState(true);
  const [csvUpload, setCsvUpload] = useState(null);
  const [isCommittingCsv, setIsCommittingCsv] = useState(false);

  useEffect(() => {
    fetchAdjustments();
    getBrandBasicObjects().then(brands => {
      setBrandOptions(brands.map(b => ({ label: b.name, sublabel: b.domain, value: b.id })));
    });
  }, []);

  const fetchAdjustments = () => {
    setIsFetching(true);
    getManualOrderAdjustments()
      .then(data => setAdjustments(data.commissions))
      .catch(err => cogoToast.error(err || 'Failed to load adjustments.'))
      .finally(() => setIsFetching(false));
  };

  const searchForCreator = async searchValue => {
    const { hits } = await searchIndex.search(searchValue, { hitsPerPage: 30 });
    return hits.map(user => ({ label: user.name, sublabels: [user.username, user.email].filter(Boolean), value: user.id }));
  };

  const handleChange = (field, value) => {
    setForm(prev => ({ ...prev, [field]: value }));
  };

  const canSubmit =
    selectedBrand && selectedCreator && form.order_id && form.order_amount && form.commission_rate && form.transaction_date && !isSubmitting;

  const handleSubmit = () => {
    if (!canSubmit) return;
    setIsSubmitting(true);
    createManualOrderAdjustment({
      Brand_id: selectedBrand.value,
      User_id: selectedCreator.value,
      order_id: form.order_id,
      order_amount: parseFloat(form.order_amount),
      commission_rate: parseFloat(form.commission_rate),
      transaction_date: form.transaction_date,
      currency: selectedCurrency.value,
      code: form.code.trim() || undefined
    })
      .then(() => {
        cogoToast.success('Manual order adjustment created.');
        setForm(EMPTY_FORM);
        setSelectedBrand(null);
        setSelectedCreator(null);
        setSelectedCurrency(DEFAULT_CURRENCY);
        fetchAdjustments();
      })
      .catch(err => cogoToast.error(err || 'Failed to create adjustment.'))
      .finally(() => setIsSubmitting(false));
  };

  const closeCsvModal = () => {
    setCsvUpload(null);
    if (csvInputRef.current) csvInputRef.current.value = '';
  };

  const handleCsvFile = event => {
    const file = event.target.files?.[0];
    if (!file) return;

    setCsvUpload({ fileName: file.name, csv: null, preview: null, error: null });
    const reader = new FileReader();
    reader.onload = () => {
      const csv = String(reader.result || '');
      setCsvUpload({ fileName: file.name, csv, preview: null, error: null });
      previewManualOrderAdjustmentsCsv(csv)
        .then(preview => setCsvUpload(current => (current?.csv === csv ? { ...current, preview } : current)))
        .catch(error =>
          setCsvUpload(current => (current?.csv === csv ? { ...current, error: error || 'Failed to preview the CSV.' } : current))
        );
    };
    reader.onerror = () => setCsvUpload(current => (current ? { ...current, error: 'Failed to read the CSV file.' } : current));
    reader.readAsText(file);
  };

  const handleCommitCsv = () => {
    if (!csvUpload?.csv || !csvUpload.preview || csvUpload.preview.summary.invalidCount || isCommittingCsv) return;
    setIsCommittingCsv(true);
    setCsvUpload(current => ({ ...current, error: null }));
    createManualOrderAdjustmentsBulk(csvUpload.csv)
      .then(data => {
        const count = data.createdCount;
        cogoToast.success(`${count} manual order${count === 1 ? '' : 's'} created.`);
        closeCsvModal();
        fetchAdjustments();
      })
      .catch(error => setCsvUpload(current => ({ ...current, error: error || 'Failed to create orders from the CSV.' })))
      .finally(() => setIsCommittingCsv(false));
  };

  const parsedAmount = parseFloat(form.order_amount);
  const parsedRate = parseFloat(form.commission_rate);
  const commissionPreview =
    !isNaN(parsedAmount) && !isNaN(parsedRate) ? ((parsedAmount * parsedRate) / 100).toFixed(2) : null;

  return (
    <div className='manual-order-adjustments-outer'>
      <div className='manual-order-adjustments-inner'>
        <div className='manual-order-adjustments-title'>Manual Order Adjustments</div>
        <p className='manual-order-adjustments-subtitle'>
          Use this page to retroactively credit affiliate commissions when order tracking breaks, or to void the owed commissions of a banned or
          code-leaking creator. Every change is logged for auditability.
        </p>

        <div className='manual-order-adjustments-form'>
          <div className='manual-order-adjustments-form-header'>
            <div className='manual-order-adjustments-section-title'>New Order</div>
            <button className='manual-order-adjustments-btn manual-order-adjustments-btn-secondary' onClick={() => csvInputRef.current?.click()}>
              Upload CSV
            </button>
            <input ref={csvInputRef} hidden type='file' accept='.csv,text/csv' onChange={handleCsvFile} />
          </div>
          <div className='manual-order-adjustments-csv-help'>
            CSV requires <strong>Brand_id, order_id, order_amount, commission_rate, transaction_date</strong> and either <strong>User_id</strong> or{' '}
            <strong>username</strong>. <strong>currency</strong> and <strong>code</strong> are optional; other columns are ignored. Enter rates as
            percentages (50 = 50%). Currency defaults to USD.
          </div>
          <div className='manual-order-adjustments-fields'>
            <div className='manual-order-adjustments-field'>
              <label className='manual-order-adjustments-label'>
                Brand<span className='manual-order-adjustments-required'>*</span>
              </label>
              <Select
                className='manual-order-adjustments-select'
                placeholder='Search by brand name...'
                options={brandOptions}
                value={selectedBrand}
                onChange={setSelectedBrand}
                isClearable
                isSearchable
                components={{ Option: SelectOption }}
              />
            </div>
            <div className='manual-order-adjustments-field'>
              <label className='manual-order-adjustments-label'>
                Creator<span className='manual-order-adjustments-required'>*</span>
              </label>
              <AsyncSelect
                className='manual-order-adjustments-select'
                placeholder='Search by creator name...'
                loadOptions={searchForCreator}
                value={selectedCreator}
                onChange={setSelectedCreator}
                isClearable
                cacheOptions
                defaultOptions={false}
                components={{ Option: SelectOption }}
              />
            </div>
            <div className='manual-order-adjustments-field'>
              <label className='manual-order-adjustments-label'>
                Order ID<span className='manual-order-adjustments-required'>*</span>
              </label>
              <input
                className='manual-order-adjustments-input'
                type='text'
                placeholder='e.g. ORD-10042'
                value={form.order_id}
                onChange={e => handleChange('order_id', e.target.value)}
              />
            </div>
            <div className='manual-order-adjustments-field'>
              <label className='manual-order-adjustments-label'>
                Order Amount<span className='manual-order-adjustments-required'>*</span>
              </label>
              <input
                className='manual-order-adjustments-input'
                type='number'
                step='0.01'
                min='0'
                placeholder='e.g. 150.00'
                value={form.order_amount}
                onChange={e => handleChange('order_amount', e.target.value)}
              />
            </div>
            <div className='manual-order-adjustments-field'>
              <label className='manual-order-adjustments-label'>
                Commission Rate (%)<span className='manual-order-adjustments-required'>*</span>
              </label>
              <input
                className='manual-order-adjustments-input'
                type='number'
                step='0.01'
                min='0'
                max='100'
                placeholder='e.g. 15'
                value={form.commission_rate}
                onChange={e => handleChange('commission_rate', e.target.value)}
              />
              {commissionPreview && (
                <div className='manual-order-adjustments-hint'>
                  Commission: {selectedCurrency.value} {commissionPreview}
                </div>
              )}
            </div>
            <div className='manual-order-adjustments-field'>
              <label className='manual-order-adjustments-label'>
                Transaction Date<span className='manual-order-adjustments-required'>*</span>
              </label>
              <input
                className='manual-order-adjustments-input'
                type='date'
                value={form.transaction_date}
                onChange={e => handleChange('transaction_date', e.target.value)}
              />
            </div>
            <div className='manual-order-adjustments-field'>
              <label className='manual-order-adjustments-label'>Currency</label>
              <Select
                className='manual-order-adjustments-select'
                options={CURRENCY_OPTIONS}
                value={selectedCurrency}
                onChange={setSelectedCurrency}
                isSearchable
              />
            </div>
            <div className='manual-order-adjustments-field'>
              <label className='manual-order-adjustments-label'>Custom Code</label>
              <input
                className='manual-order-adjustments-input'
                type='text'
                placeholder='Optional'
                value={form.code}
                onChange={e => handleChange('code', e.target.value)}
              />
            </div>
          </div>
          <button className='manual-order-adjustments-btn manual-order-adjustments-btn-primary' disabled={!canSubmit} onClick={handleSubmit}>
            {isSubmitting ? 'Submitting...' : 'Submit Order'}
          </button>
        </div>

        <VoidCreatorCommissions brandOptions={brandOptions} searchForCreator={searchForCreator} />

        <div className='manual-order-adjustments-history'>
          <div className='manual-order-adjustments-section-title'>Recent Manual Orders</div>
          {isFetching ? (
            <div className='manual-order-adjustments-loading'>Loading...</div>
          ) : adjustments.length === 0 ? (
            <div className='manual-order-adjustments-empty'>No manually-created orders found.</div>
          ) : (
            <table className='manual-order-adjustments-table'>
              <thead>
                <tr>
                  <th>ID</th>
                  <th>Brand</th>
                  <th>Creator</th>
                  <th>Order ID</th>
                  <th>Code</th>
                  <th>Order Amount</th>
                  <th>Commission</th>
                  <th>Currency</th>
                  <th>Transaction Date</th>
                  <th>Created</th>
                </tr>
              </thead>
              <tbody>
                {adjustments.map(adj => (
                  <tr key={adj.id}>
                    <td>{adj.id}</td>
                    <td>{adj.brandName || adj.Brand_id}</td>
                    <td>{adj.creatorName || adj.creatorUsername || adj.cid}</td>
                    <td>{adj.order_id}</td>
                    <td>{adj.code || '—'}</td>
                    <td>{Number(adj.order_amount).toFixed(2)}</td>
                    <td>{Number(adj.commission_amount).toFixed(2)}</td>
                    <td>{adj.currency}</td>
                    <td>{moment.utc(adj.transaction_date).format('YYYY-MM-DD')}</td>
                    <td>{moment(adj.createdAt).format('YYYY-MM-DD HH:mm')}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>

      {csvUpload && (
        <ManualOrderAdjustmentsCsvModal
          error={csvUpload.error}
          fileName={csvUpload.fileName}
          isCommitting={isCommittingCsv}
          isLoading={!csvUpload.preview && !csvUpload.error}
          preview={csvUpload.preview}
          onClose={closeCsvModal}
          onCommit={handleCommitCsv}
        />
      )}
    </div>
  );
};

export default ManualOrderAdjustments;
